import { AfterViewInit, Directive, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appNumberField]'
})
export class NumberFieldDirective implements AfterViewInit {

  @Input() minimumValue: number = 0;
  @Input() maximumValue: number = 999999;
  @Input() initialValue: number = 0;
  @Input() noOfDecimalPlaces: number = 2;
  @Input() allowDecimals: boolean = true;
  @Input() decimalSeparator: string = '.';
  @Input() defaultValidValue: number = 1;

  lastValidValue?: number;
  invalidChars = ['-', '+', 'e', 'E'];

  constructor(private control: NgControl) { }

  ngAfterViewInit() {
    this.lastValidValue = this.initialValue;

    // Bugfix for Bug 26878: Purchase Request Quantity Field Allows More Than 2 Decimal Places When Value Is Pasted
    setTimeout(() => {
      const rawValue = this.control.control?.value;
      const validatedValue = this.getValidatedValue(rawValue);
      if (validatedValue === null) return;

      const normalizedValue = typeof rawValue === 'string' ? this.stripLeadingZeros(rawValue) : rawValue;
      if (validatedValue !== Number(rawValue)) {
        this.control.control?.setValue(validatedValue);
      } else if (normalizedValue !== rawValue) {
        this.control.control?.setValue(normalizedValue);
      }
      this.lastValidValue = validatedValue;
    });
  }

  /**
  * Event handler for host's paste event
  */
  @HostListener('paste', ['$event']) onPaste(event: ClipboardEvent) {
    const value = event.clipboardData?.getData('text/plain');
    event.preventDefault();
    this.toValidValue(value ?? '');
  }

  /**
  * Event handler for host's keyup event
  */
  @HostListener('keyup', ['$event']) onKeyUp(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    if (this.invalidChars.includes(event.key) || (!this.allowDecimals && event.key === this.decimalSeparator)) {
      this.control.control?.setValue('');
      this.preventDefault(target.value);
      return;
    }

    this.normalizeLeadingZeros(target);

    if (this.allowDecimals && event.key === this.decimalSeparator)
      return;

    if (!this.isValidNumber(target['value']) ||
      (this.isDeciaml(+target['value']) && this.countDeciamlPaces(target['value']) > this.noOfDecimalPlaces)) {
      this.preventDefault(target['value']);
      return;
    }

    // Decimal
    if (this.isDeciaml(+target['value'])) {
      if (this.countDeciamlPaces(target['value']) > this.noOfDecimalPlaces) {
        this.preventDefault(target['value']);
      } else {
        const validatedValue = this.getValidatedValue(target['value']);
        if (validatedValue === null) return;
        if (validatedValue < this.minimumValue) {
          this.control.control?.setValue(this.defaultValidValue);
          this.lastValidValue = this.defaultValidValue;
        } else if (this.lastValidValue !== validatedValue) {
          this.setValue(validatedValue.toString());
        }
      }
    }
    // Integer or deciaml point
    else {
      this.setValue(target['value']);
    }
  }

  /**
  * Event handler for host's blur event
  */
  @HostListener('blur') onBlur() {
    const value = this.control.control?.value;
    if (value === null || value === undefined || value === '' || value < this.minimumValue) {
      this.control.control?.setValue(this.defaultValidValue);
      this.lastValidValue = this.defaultValidValue;
    }
  }

  countDeciamlPaces(value: string) {
    if (!value) return 0;
    const indexOfDeciamlPoint = value.indexOf(this.decimalSeparator);
    if (indexOfDeciamlPoint !== -1) {
      return value.length - (indexOfDeciamlPoint + 1);
    }
    return 0;
  }

  preventDefault(value: string) {
    const validatedValue = this.getValidatedValue(value);
    if (validatedValue !== null) {
      this.control.control?.setValue(validatedValue);
      this.lastValidValue = validatedValue;
    } else {
      this.control.control?.setValue(this.lastValidValue);
    }
  }

  setValue(value: string) {
    const emptyNumberRegex = new RegExp(`^0\\.0{0,${this.noOfDecimalPlaces - 1}}$`);
    if (emptyNumberRegex.test(value)) return;
    this.lastValidValue = +value;
  }

  /**
   * Removes the redundant leading zeros (e.g. '000040.0' => '40.0'). The lookahead only trims zeros
   * followed by a digit, so a lone '0' and a leading '0.' are kept while the user is still typing.
   * @param value
   */
  private stripLeadingZeros(value: string): string {
    return value.replace(/^0+(?=\d)/, '');
  }

  /**
   * Rewrites the host's value without its redundant leading zeros, keeping the caret in place
   * @param target
   */
  private normalizeLeadingZeros(target: HTMLInputElement): void {
    const normalizedValue = this.stripLeadingZeros(target.value);
    const removedCharsCount = target.value.length - normalizedValue.length;
    if (removedCharsCount === 0) return;

    const caretPosition = Math.max(0, (target.selectionStart ?? 0) - removedCharsCount);
    this.control.control?.setValue(normalizedValue);
    target.value = normalizedValue;
    if (target.type !== 'number') target.setSelectionRange(caretPosition, caretPosition);
  }

  /**
   * Check if the value is a valid number (integer or decimal)
   * @param value
   */
  private isValidNumber(value: string): boolean {
    const valueNumber = Number(value);
    return !isNaN(valueNumber) && (this.maximumValue === undefined || valueNumber <= this.maximumValue);
  }

  private isDeciaml(value: number) {
    return value % 1 !== 0;
  }

  private toValidValue(value: string): void {
    const validatedValue = this.getValidatedValue(value);
    if (validatedValue === null) return;
    this.control.control?.setValue(validatedValue);
    this.lastValidValue = validatedValue;
  }

  private getValidatedValue(value: string): number | null {
    const numericValue = Number(value);
    if (isNaN(numericValue) || value === null || value === undefined || value === '') return null;
    if (numericValue > this.maximumValue || numericValue < this.minimumValue || this.invalidChars.some(char => value.toString().includes(char))) {
      return this.defaultValidValue;
    }
    if (this.isDeciaml(numericValue) && this.countDeciamlPaces(value.toString()) > this.noOfDecimalPlaces) {
      const factor = Math.pow(10, this.noOfDecimalPlaces);
      const truncated = Math.trunc(numericValue * factor) / factor;
      return truncated < this.minimumValue ? this.defaultValidValue : truncated;
    }
    return numericValue;
  }
}
